import styled, { css, keyframes } from "styled-components";
import { BRAND_C as C } from "../../common/colors";

// ─── Design Tokens (imported from ./colors.js) ────────────────────────────────
// To change any color, edit BRAND_C in colors.js — it cascades here automatically.

// ─── Keyframes ────────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(28px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const floatBob = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  33%       { transform: translateY(-10px) rotate(0.8deg); }
  66%       { transform: translateY(-5px) rotate(-0.5deg); }
`;

const spinSlow = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const counterSpin = keyframes`
  from { transform: rotate(360deg); }
  to   { transform: rotate(0deg); }
`;

const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position:  200% center; }
`;

// ─── Page Shell ───────────────────────────────────────────────────────────────
export const MainDiv = styled.div`
  position: relative;
  overflow: hidden;
  background: #fdfcff;
  padding-bottom: 0;

  /* react-slick dot theming */
  .slick-dots li button::before {
    color: ${C.brand};
    opacity: 0.35;
    font-size: 9px;
  }
  .slick-dots li.slick-active button::before {
    color: ${C.brand};
    opacity: 1;
  }
  .slick-dots {
    bottom: 18px;
  }
`;

// ─── Background Decoration ────────────────────────────────────────────────────
export const BgBlob = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  pointer-events: none;
  z-index: 0;
  &.s1 { width: 520px; height: 520px; background: rgba(108,71,255,0.09); top: -120px; left: -140px; }
  &.s2 { width: 380px; height: 380px; background: rgba(245,197,24,0.07); top: 30%; right: -100px; }
  &.s3 { width: 260px; height: 260px; background: rgba(108,71,255,0.06); bottom: 60px; left: 40%; }
`;

export const DotGrid = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  background-image: radial-gradient(circle, rgba(108,71,255,0.07) 1px, transparent 1px);
  background-size: 28px 28px;
  mask-image: linear-gradient(to bottom, transparent, rgba(0,0,0,0.4) 20%, rgba(0,0,0,0.4) 80%, transparent);
`;

// ─── Wave Transition (bottom) ─────────────────────────────────────────────────
export const Wave2 = styled.div`
  display: block;
  position: relative;
  height: 60px;
  width: 100%;
  background: #fdfcff;
  z-index: 2;

  &::before {
    content: "";
    display: block;
    position: absolute;
    border-radius: 50%;
    width: 110%;
    height: 300px;
    background: #fdfcff;
    left: -5%;
    top: -240px;
    clip-path: ellipse(55% 28% at 50% 100%);
  }
`;

// ─── Slide Container ──────────────────────────────────────────────────────────
export const MainContainer = styled.div`
  position: relative;
  z-index: 1;
  min-height: 75vh;
  display: flex !important;
  align-items: center;
  padding: 60px 20px 80px;
  animation: ${fadeUp} 0.65s ease both;

  @media (max-width: 800px) {
    min-height: auto;
    padding: 40px 16px 70px;
  }
`;

// ─── Slide Layout ─────────────────────────────────────────────────────────────
export const Container0 = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 60px;
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;

  /* Image right, text left on desktop */
  flex-direction: row;

  @media (max-width: 800px) {
    flex-direction: column-reverse;
    gap: 28px;
  }
`;

// ─── Text Side ────────────────────────────────────────────────────────────────
export const Container2 = styled.div`
  flex: 1;
  max-width: 480px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 800px) {
    max-width: 100%;
    text-align: center;
    align-items: center;
  }
`;

export const Container4 = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  @media (max-width: 800px) {
    align-items: center;
  }
`;

// ─── Eyebrow / Hi ─────────────────────────────────────────────────────────────
export const Hi = styled.p`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${C.brand};
  margin-bottom: 4px;
`;

// ─── Name ─────────────────────────────────────────────────────────────────────
export const Name = styled.h2`
  font-size: clamp(2rem, 5vw, 3.2rem);
  font-weight: 800;
  color: ${C.text};
  line-height: 1.12;
  letter-spacing: -0.025em;
  margin: 0;

  /* Shimmer accent on first slide */
  background: linear-gradient(
    110deg,
    ${C.text} 30%,
    ${C.brand} 50%,
    ${C.text} 70%
  );
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${shimmer} 5s linear infinite;
`;

// ─── Profession ───────────────────────────────────────────────────────────────
export const NameDivider = styled.div`
  width: 44px;
  height: 3px;
  background: linear-gradient(90deg, ${C.brand}, ${C.accent});
  border-radius: 2px;
  margin: 6px 0;

  @media (max-width: 800px) {
    margin: 6px auto;
  }
`;

export const Profession = styled.div`
  font-size: clamp(0.9rem, 1.8vw, 1.05rem);
  font-weight: 600;
  color: ${C.brandLight};
  letter-spacing: 0.01em;
`;

// ─── Testimonial ──────────────────────────────────────────────────────────────
export const Testimonial = styled.p`
  font-size: clamp(0.88rem, 1.6vw, 0.97rem);
  color: ${C.textMid};
  line-height: 1.75;
  margin: 6px 0 16px;
  max-width: 420px;

  @media (max-width: 800px) {
    max-width: 100%;
    margin: 6px 0 14px;
  }
`;

// ─── Photo Area ───────────────────────────────────────────────────────────────
export const PhotoContainer = styled.div`
  position: relative;
  flex-shrink: 0;
  width: clamp(220px, 32vw, 360px);
  height: clamp(220px, 32vw, 360px);

  @media (max-width: 800px) {
    width: clamp(160px, 50vw, 240px);
    height: clamp(160px, 50vw, 240px);
  }
`;

/* Rotating ring behind the photo */
export const SpinRing = styled.div`
  position: absolute;
  inset: -12px;
  border-radius: 50%;
  border: 2.5px dashed ${C.border};
  animation: ${spinSlow} 18s linear infinite;
`;

export const SpinRingAccent = styled.div`
  position: absolute;
  inset: -24px;
  border-radius: 50%;
  border: 2px dashed rgba(245,197,24,0.22);
  animation: ${counterSpin} 26s linear infinite;
`;

/* Purple glow behind the avatar */
export const PhotoGlow = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: radial-gradient(circle at 40% 40%, rgba(108,71,255,0.22), transparent 70%);
  filter: blur(24px);
  z-index: 0;
`;

export const Photo1 = styled.img`
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  object-position: center top;
  border: 4px solid ${C.surface};
  box-shadow:
    0 8px 32px rgba(108,71,255,0.22),
    0 2px 8px rgba(0,0,0,0.08);
  animation: ${floatBob} 6s ease-in-out infinite;
  display: block;
`;

// ─── CTA Button ───────────────────────────────────────────────────────────────
export const SliderBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 13px 26px;
  background: ${C.brand};
  color: #ffffff;
  border: none;
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  box-shadow: 0 4px 16px rgba(108,71,255,0.30);
  margin-top: 4px;
  align-self: flex-start;

  svg { width: 15px; height: 15px; transition: transform 0.2s; }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(108,71,255,0.40);
    svg { transform: translateX(3px); }
  }
  &:active { transform: translateY(0); }
  &:focus-visible { outline: 3px solid ${C.accent}; outline-offset: 3px; }

  @media (max-width: 800px) {
    align-self: center;
    padding: 12px 22px;
  }
`;